// The administrator's list of users: filters, sorting and the figures of the top row.
// Administrators have no subscription, so they are left out of every figure and filter except "all".
import { ACCOUNT_STATUS } from '../constants/accountStatus';

const DAY = 86400000;

export const NO_FILTER = { status: 'all', type: 'all', activity: 'all', quick: 'all' };

// "Срезы": quick filters that are not a status or a type.
export const QUICK_LABELS = {
  all: 'Без среза',
  trial_running: 'На пробном',
  lapsed: 'Пробный закончился, не купил',
  unverified: 'Почта не подтверждена',
  expiring: 'Доступ истекает за 7 дней',
  paid: 'Когда-либо платили',
  new1: 'Зарегистрировались сегодня',
  new7: 'Зарегистрировались за 7 дней',
  new30: 'Зарегистрировались за 30 дней',
};

const startOfToday = (now) => { const d = new Date(now); d.setHours(0, 0, 0, 0); return d.getTime(); };

const daysLeft = (u, now) => (u.expires_at ? (new Date(u.expires_at).getTime() - now) / DAY : null);

const quickMatch = (u, quick, now) => {
  const created = new Date(u.created_at).getTime();
  switch (quick) {
    case 'trial_running': return u.status === ACCOUNT_STATUS.ACTIVE && Boolean(u.is_trial);
    case 'lapsed': return Boolean(u.trial_lapsed);
    case 'unverified': return !u.email_verified_at;
    case 'expiring': { const d = daysLeft(u, now); return u.status === ACCOUNT_STATUS.ACTIVE && d !== null && d <= 7; }
    case 'paid': return Boolean(u.ever_paid);
    case 'new1': return created >= startOfToday(now);
    case 'new7': return created >= now - 7 * DAY;
    case 'new30': return created >= now - 30 * DAY;
    default: return true;
  }
};

// filters = { search, status, type, activity, quick }
export function userMatches(u, filters, now = Date.now()) {
  const f = { ...NO_FILTER, search: '', ...filters };
  if (!u.email.toLowerCase().includes(f.search.trim().toLowerCase())) return false;
  const restricted = f.status !== 'all' || f.type !== 'all' || f.activity !== 'all' || f.quick !== 'all';
  if (!restricted) return true;
  if (u.role === 'admin') return false;
  if (f.status !== 'all' && u.status !== f.status) return false;
  if (f.type !== 'all' && u.account_type !== f.type) return false;
  if (f.activity === 'active' && u.status !== ACCOUNT_STATUS.ACTIVE) return false;
  if (f.activity === 'inactive' && u.status === ACCOUNT_STATUS.ACTIVE) return false;
  return quickMatch(u, f.quick, now);
}

const SORT_VALUE = {
  email: (u) => u.email.toLowerCase(),
  type: (u) => (u.role === 'admin' ? 'я' : u.account_type || ''),
  status: (u) => u.status || '',
  // people without a date go to the end in either direction (see sortUsers)
  expires: (u) => (u.activated_at ? (u.expires_at ? new Date(u.expires_at).getTime() : Infinity) : null),
  created: (u) => new Date(u.created_at).getTime(),
  login: (u) => (u.last_login_at ? new Date(u.last_login_at).getTime() : null),
};

export function sortUsers(list, key, dir = 'asc') {
  const value = SORT_VALUE[key];
  if (!value) return list;
  const sign = dir === 'desc' ? -1 : 1;
  return [...list].sort((a, b) => {
    const x = value(a);
    const y = value(b);
    if (x === null && y === null) return 0;
    if (x === null) return 1; // no value: always last
    if (y === null) return -1;
    if (x < y) return -1 * sign;
    if (x > y) return 1 * sign;
    return 0;
  });
}

// The figures of the top row; each tile is also a preset of the filters.
export function summarize(users, now = Date.now()) {
  const people = users.filter((u) => u.role !== 'admin');
  const count = (fn) => people.filter(fn).length;
  const byStatus = (s) => count((u) => u.status === s);
  const byType = (t) => count((u) => u.account_type === t);
  const quick = (q) => count((u) => quickMatch(u, q, now));
  return {
    all: users.length,
    admins: users.length - people.length,
    people: people.length,
    groups: [
      { title: 'Кто они', tiles: [
        { key: 'people', label: 'Всего', value: people.length, filter: NO_FILTER },
        { key: 'user', label: 'Пользователи', value: byType('user'), filter: { ...NO_FILTER, type: 'user' } },
        { key: 'seller', label: 'Селлеры', value: byType('seller'), filter: { ...NO_FILTER, type: 'seller' } },
        { key: 'agent', label: 'Агенты', value: byType('agent'), filter: { ...NO_FILTER, type: 'agent' } },
        { key: 'seller_agent', label: 'Селлер · Агент', value: byType('seller_agent'), filter: { ...NO_FILTER, type: 'seller_agent' } },
      ] },
      { title: 'Доступ', tiles: [
        { key: 'active', label: 'Активные', value: byStatus(ACCOUNT_STATUS.ACTIVE), tone: 'success', filter: { ...NO_FILTER, activity: 'active' } },
        { key: 'inactive', label: 'Неактивные', value: count((u) => u.status !== ACCOUNT_STATUS.ACTIVE), filter: { ...NO_FILTER, activity: 'inactive' } },
        { key: 'trial_running', label: 'На пробном', value: quick('trial_running'), filter: { ...NO_FILTER, quick: 'trial_running' } },
        { key: 'lapsed', label: 'Пробный закончился', value: quick('lapsed'), tone: 'warning', hint: 'не купили: кандидаты для продаж', filter: { ...NO_FILTER, quick: 'lapsed' } },
        { key: 'unverified', label: 'Ждут почты', value: quick('unverified'), hint: 'почта не подтверждена', filter: { ...NO_FILTER, quick: 'unverified' } },
        { key: 'pending', label: 'Ждут доступа', value: byStatus(ACCOUNT_STATUS.PENDING_ACTIVATION), tone: 'warning', filter: { ...NO_FILTER, status: ACCOUNT_STATUS.PENDING_ACTIVATION } },
        { key: 'expired', label: 'Подписка истекла', value: byStatus(ACCOUNT_STATUS.SUBSCRIPTION_EXPIRED), tone: 'error', filter: { ...NO_FILTER, status: ACCOUNT_STATUS.SUBSCRIPTION_EXPIRED } },
        { key: 'expiring', label: 'Истекает за 7 дн.', value: quick('expiring'), filter: { ...NO_FILTER, quick: 'expiring' } },
      ] },
      { title: 'Деньги и приток', tiles: [
        { key: 'paid', label: 'Платили', value: quick('paid'), tone: 'success', hint: 'когда-либо', filter: { ...NO_FILTER, quick: 'paid' } },
        { key: 'new1', label: 'Сегодня', value: quick('new1'), hint: 'регистраций', filter: { ...NO_FILTER, quick: 'new1' } },
        { key: 'new7', label: 'За 7 дней', value: quick('new7'), hint: 'регистраций', filter: { ...NO_FILTER, quick: 'new7' } },
        { key: 'new30', label: 'За 30 дней', value: quick('new30'), hint: 'регистраций', filter: { ...NO_FILTER, quick: 'new30' } },
      ] },
    ],
  };
}

export const sameFilters = (a, b) => ['status', 'type', 'activity', 'quick'].every((k) => (a[k] || 'all') === (b[k] || 'all'));
